import React, { useEffect, useState } from 'react'
import { shipmentsService } from '../services/shipmentsService'
import { useApp } from '../contexts/AppContext'

const OPERATION_STYLES = {
    create: 'bg-[#eafaf1] text-[#27ae60] border border-[#27ae60]/20',
    update: 'bg-[#eaf4fb] text-[#3498db] border border-[#3498db]/20',
    delete: 'bg-[#fdedec] text-[#e74c3c] border border-[#e74c3c]/20',
}

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return '—'
    }
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value, null, 2)
        } catch (error) {
            return String(value)
        }
    }
    return String(value)
}

const ShipmentLogsDrawer = ({ open, onClose, shipmentId }) => {
    const { users } = useApp()
    const [logs, setLogs] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!open || !shipmentId) return

        const fetchLogs = async () => {
            setLoading(true)
            setError('')
            try {
                const result = await shipmentsService.getShipmentLogs(shipmentId, 100)

                if (result.success) {
                    setLogs(result.data || [])
                } else {
                    setError(result.message || 'Failed to load shipment logs')
                }
            } catch (err) {
                setError(err.message || 'Failed to load shipment logs')
            } finally {
                setLoading(false)
            }
        }

        fetchLogs()
    }, [open, shipmentId])

    const getChangedByLabel = (log) => {
        const matchedUser = users?.find?.((user) => user?.id === log.changedBy)
        const email = (log.changedByEmail || matchedUser?.email || '').trim()
        const isSuperAdmin = log.changedBy === 126 || email.toLowerCase() === 'muhammad.asad@cargo360pk.com'

        if (isSuperAdmin) {
            return 'Super Admin'
        }

        if (email) {
            return email
        }

        if (log.changedBy) {
            return `User #${log.changedBy}`
        }

        return 'Unknown user'
    }

    if (!open) return null

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div
                className="absolute inset-0 bg-black/40"
                onClick={onClose}
                aria-hidden="true"
            />

            <aside className="relative h-full w-[480px] bg-white shadow-2xl flex flex-col">
                <header className="sticky top-0 z-10 bg-white px-6 py-5 border-b border-[#e9ecef] flex items-center justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-wide text-[#7f8c8d]">Shipment #{shipmentId}</p>
                        <h2 className="text-lg font-semibold text-[#2c3e50]">Change History</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-[#7f8c8d] hover:text-[#2c3e50] text-2xl leading-none transition-colors"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4 bg-[#f8f9fa]">
                    {loading && (
                        <div className="flex h-full items-center justify-center">
                            <div className="w-6 h-6 border-4 border-[#3498db] border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}

                    {!loading && error && (
                        <div className="bg-[#fdedec] border border-[#e74c3c]/30 text-[#e74c3c] px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    {!loading && !error && logs.filter((log) => log.operation !== 'create').length === 0 && (
                        <div className="text-center text-[#7f8c8d] text-sm py-10">
                            No logs found for this shipment.
                        </div>
                    )}

                    {!loading && !error && logs.filter((log) => log.operation !== 'create').length > 0 && (
                        <div className="relative">
                            <div className="absolute left-3 top-0 bottom-0 border-l-2 border-[#e9ecef]" aria-hidden="true"></div>
                            <div className="space-y-6">
                                {logs
                                    .filter((log) => log.operation !== 'create')
                                    .map((log) => {
                                        const diffEntries = Object.entries(log.diff || {})
                                        const operationStyle = OPERATION_STYLES[log.operation] || OPERATION_STYLES.update

                                        return (
                                            <div key={log.id} className="relative pl-10">
                                                <div className="absolute left-0 top-3 w-6 h-6 bg-white border-2 border-[#3498db] rounded-full flex items-center justify-center">
                                                    <span className="w-2 h-2 bg-[#3498db] rounded-full" />
                                                </div>

                                                <div className="bg-white rounded-lg shadow-sm border border-[#e9ecef] p-4 space-y-3">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <p className="text-sm text-[#7f8c8d]">
                                                            {new Date(log.createdAt).toLocaleString()}
                                                        </p>
                                                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${operationStyle}`}>
                                                            {log.operation?.toUpperCase()}
                                                        </span>
                                                        <span className="text-xs text-[#7f8c8d]">
                                                            Updated by {getChangedByLabel(log)}
                                                        </span>
                                                    </div>

                                                    {diffEntries.length > 0 ? (
                                                        <div className="border border-[#e9ecef] rounded-lg divide-y divide-[#e9ecef]">
                                                            {diffEntries.map(([field, values]) => (
                                                                <div key={field} className="p-3 grid grid-cols-1 gap-1">
                                                                    <p className="text-xs font-semibold text-[#7f8c8d] uppercase tracking-wide">{field}</p>
                                                                    <div className="flex items-center gap-2 text-sm text-[#2c3e50]">
                                                                        <span className="flex-1 bg-[#f8f9fa] rounded px-2 py-1">{formatValue(values?.old)}</span>
                                                                        <span className="text-[#3498db] text-lg leading-none">→</span>
                                                                        <span className="flex-1 bg-[#f8f9fa] rounded px-2 py-1">{formatValue(values?.new)}</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-[#7f8c8d] italic">
                                                            No field-level changes recorded.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })}
                            </div>
                        </div>
                    )}
                </div>
            </aside>
        </div>
    )
}

export default ShipmentLogsDrawer

